import React from 'react';
import Icon from '../../../components/AppIcon';

const TRLSummaryPanel = ({ summaryData }) => {
  const getTRLColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const getTRLBgColor = (score) => {
    if (score >= 80) return 'bg-success/10 border-success/20';
    if (score >= 60) return 'bg-warning/10 border-warning/20';
    return 'bg-error/10 border-error/20';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'text-error bg-error/10';
      case 'Medium': return 'text-warning bg-warning/10';
      case 'Low': return 'text-success bg-success/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  return (
    <div className="space-y-6">
      {/* Overall TRL Summary */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="BarChart3" size={20} className="mr-2" />
          TRL Overview
        </h3>
        
        <div className="space-y-4">
          <div className={`p-4 rounded-lg border ${getTRLBgColor(summaryData?.averageTRL)}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Average TRL Score</span>
              <span className={`text-2xl font-bold ${getTRLColor(summaryData?.averageTRL)}`}>
                {summaryData?.averageTRL}%
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${
                  summaryData?.averageTRL >= 80 ? 'bg-success' :
                  summaryData?.averageTRL >= 60 ? 'bg-warning' : 'bg-error'
                }`}
                style={{ width: `${summaryData?.averageTRL}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 bg-success/10 rounded-lg">
              <p className="text-2xl font-bold text-success">{summaryData?.readyTransactions}</p>
              <p className="text-xs text-muted-foreground">Ready (80%+)</p>
            </div>
            <div className="text-center p-3 bg-warning/10 rounded-lg">
              <p className="text-2xl font-bold text-warning">{summaryData?.inProgressTransactions}</p>
              <p className="text-xs text-muted-foreground">In Progress</p>
            </div>
            <div className="text-center p-3 bg-error/10 rounded-lg">
              <p className="text-2xl font-bold text-error">{summaryData?.blockedTransactions}</p>
              <p className="text-xs text-muted-foreground">Blocked</p>
            </div>
          </div>
        </div>
      </div>
      {/* Pending Documents */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="FileText" size={20} className="mr-2" />
          Pending Documents
          <span className="ml-2 px-2 py-1 text-xs bg-warning text-warning-foreground rounded-full">
            {summaryData?.pendingDocuments?.length}
          </span>
        </h3>
        
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {summaryData?.pendingDocuments?.map((doc, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="Clock" size={16} className="text-warning" />
                <div>
                  <p className="text-sm font-medium text-foreground">{doc?.name}</p>
                  <p className="text-xs text-muted-foreground">{doc?.transaction}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">{doc?.assignedTo}</p>
                <p className="text-xs text-muted-foreground">{doc?.dueDate}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Upcoming Deadlines */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Calendar" size={20} className="mr-2" />
          Upcoming Deadlines
        </h3>
        
        <div className="space-y-3">
          {summaryData?.upcomingDeadlines?.map((deadline, index) => (
            <div key={index} className="flex items-center justify-between p-3 border border-border rounded-lg">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${
                  deadline?.priority === 'High' ? 'bg-error' :
                  deadline?.priority === 'Medium' ? 'bg-warning' : 'bg-success'
                }`} />
                <div>
                  <p className="text-sm font-medium text-foreground">{deadline?.task}</p>
                  <p className="text-xs text-muted-foreground">{deadline?.transaction}</p>
                </div>
              </div>
              <div className="text-right">
                <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(deadline?.priority)}`}>
                  {deadline?.priority}
                </span>
                <p className="text-xs text-muted-foreground mt-1">{deadline?.dueDate}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Blockchain Activity */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Shield" size={20} className="mr-2 text-success" />
          Recent Blockchain Activity
        </h3>
        
        <div className="space-y-3">
          {summaryData?.blockchainActivity?.map((activity, index) => (
            <div key={index} className="flex items-center space-x-3 p-3 bg-success/5 border border-success/20 rounded-lg">
              <Icon name="Check" size={16} className="text-success" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">{activity?.action}</p>
                <p className="text-xs text-muted-foreground">{activity?.transaction}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">{activity?.timestamp}</p>
                <p className="text-xs font-mono text-muted-foreground">{activity?.hash}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Performance Metrics */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="TrendingUp" size={20} className="mr-2" />
          Performance Metrics
        </h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <p className="text-2xl font-bold text-foreground">{summaryData?.avgCompletionTime}</p>
            <p className="text-xs text-muted-foreground">Avg. Completion Time</p>
          </div>
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <p className="text-2xl font-bold text-foreground">{summaryData?.stakeholderEfficiency}%</p>
            <p className="text-xs text-muted-foreground">Stakeholder Efficiency</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TRLSummaryPanel;